import React, { useCallback, useEffect, useContext } from 'react';
import { useMembersHeaderCellQuery } from './MembersHeaderCellQuery.generated';

import { HeaderCellButton } from './HeaderCellButton';
import { Popover, usePopover, PopoverScreen } from '@trello/nachos/popover';
import { forTemplate } from '@trello/i18n';

const format = forTemplate('multi_board_table_view');

import { SavedViewContext, SavedViewEditable } from './SavedViewContext';
import { Analytics } from '@trello/atlassian-analytics';
import { MembersFilterPopoverContent } from './filters/MembersFilterPopoverContent';

enum Screen {
  MembersFilterScreen,
}

interface MemberHeaderCellProps {
  idBoards: string[];
  idOrg: string;
}

const MembersHeaderCellEditable: React.FC<
  MemberHeaderCellProps & { savedView: SavedViewEditable }
> = ({ idBoards, idOrg, savedView }) => {
  const { toggle, triggerRef, popoverProps } = usePopover<HTMLDivElement>({
    initialScreen: Screen.MembersFilterScreen,
  });

  useEffect(() => {
    if (popoverProps.isVisible) {
      Analytics.sendScreenEvent({
        name: 'memberFilterInlineDialog',
        containers: {
          organization: {
            id: idOrg,
          },
        },
      });
    }
  }, [popoverProps.isVisible, idOrg]);

  const { data, error, loading } = useMembersHeaderCellQuery({
    fetchPolicy: 'cache-first',
    variables: {
      idOrg,
      idBoards,
    },
  });

  useEffect(() => {
    if (error) {
      console.error(error);
    }
  }, [error]);

  const boards = data?.organization?.boards;

  const toggleMemberPopover = useCallback(() => {
    Analytics.sendClickedButtonEvent({
      source: 'multiBoardTableViewScreen',
      buttonName: 'filterTableByMemberButton',
      containers: {
        organization: {
          id: idOrg,
        },
      },
    });

    toggle();
  }, [toggle, idOrg]);

  return (
    <>
      <HeaderCellButton
        heading={format('members')}
        onClick={toggleMemberPopover}
        isClickable
        isFilterActive={savedView.filters.members.filterLength() !== 0}
        isSortingActive={false}
        forwardedRef={triggerRef}
        isActive={popoverProps.isVisible}
      />
      <Popover
        title={format('members')}
        dangerous_disableHideOnUrlSearchParamsChange
        dontOverlapAnchorElement
        {...popoverProps}
      >
        <PopoverScreen id={Screen.MembersFilterScreen}>
          <MembersFilterPopoverContent
            showResetButton={true}
            popoverTarget="headerCell"
            idOrg={idOrg}
            membersFilter={savedView.filters.members}
            setMembersFilter={savedView.setFilter}
            loading={loading}
            error={!!error}
            boards={boards}
          ></MembersFilterPopoverContent>
        </PopoverScreen>
      </Popover>
    </>
  );
};

export const MembersHeaderCell: React.FunctionComponent<MemberHeaderCellProps> = ({
  idBoards,
  idOrg,
}) => {
  const { savedView } = useContext(SavedViewContext);

  return savedView.editable ? (
    <MembersHeaderCellEditable
      idBoards={idBoards}
      idOrg={idOrg}
      savedView={savedView}
    />
  ) : (
    <HeaderCellButton
      heading={format('members')}
      isFilterActive={savedView.filters.members.filterLength() !== 0}
      isSortingActive={false}
    />
  );
};
